import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, User, LogOut, LayoutDashboard, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isAuthenticated } = useAuth();

  const navLinks = [
    { href: '/buscar', label: 'Buscar Profesional' },
    { href: '/rangos', label: 'Rangos' },
    { href: '/oferta-academica', label: 'Oferta Académica' },
    { href: '/membresia', label: 'Membresía' },
    { href: '/como-funciona', label: 'Cómo Funciona' },
    { href: '/asociaciones', label: 'Asociaciones' },
    { href: '/reglas-marca', label: 'Reglas de Marca' },
    { href: '/contacto', label: 'Contacto' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-[#111111]/95 backdrop-blur-lg border-b border-[#2a2a2a]" data-testid="navbar">
      <div className="container-cni">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <img 
              src="/images/logo-cni.png" 
              alt="Colegio Nacional de Inmobiliarios" 
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="navbar-desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'text-amber-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-[#2a2a2a]">
                    <User className="h-4 w-4 mr-2" />
                    {user?.nombre?.split(' ')[0]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#1a1a1a] border-[#2a2a2a] text-white">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} className="hover:bg-[#2a2a2a] cursor-pointer">
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Mi Dashboard
                  </DropdownMenuItem>
                  {isAdmin() && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="hover:bg-[#2a2a2a] cursor-pointer">
                      <Shield className="h-4 w-4 mr-2" />
                      Panel Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                  <DropdownMenuItem onClick={handleLogout} className="hover:bg-[#2a2a2a] cursor-pointer text-red-400">
                    <LogOut className="h-4 w-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-[#2a2a2a]">
                    Iniciar Sesión
                  </Button>
                </Link>
                <Link to="/registro">
                  <Button className="bg-amber-500 hover:bg-amber-400 text-black font-semibold">
                    Unirme al Colegio
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-400 hover:text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="navbar-mobile-toggle"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-[#2a2a2a] py-4" data-testid="navbar-mobile-menu">
            <nav className="space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`block px-4 py-2 text-sm rounded-md ${
                    isActive(link.href)
                      ? 'text-amber-400 bg-amber-500/10'
                      : 'text-gray-400 hover:text-white hover:bg-[#1a1a1a]'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 px-4 space-y-2">
                {isAuthenticated ? (
                  <>
                    <Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>
                      <Button className="w-full bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white">
                        Mi Dashboard
                      </Button>
                    </Link>
                    <Button onClick={handleLogout} variant="outline" className="w-full border-[#2a2a2a] text-red-400">
                      Cerrar Sesión
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                      <Button className="w-full bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white">
                        Iniciar Sesión
                      </Button>
                    </Link>
                    <Link to="/registro" onClick={() => setIsMenuOpen(false)}>
                      <Button className="w-full bg-amber-500 hover:bg-amber-400 text-black">
                        Unirme al Colegio
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
